import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";

export function SearchableDropdownFilter({
  options,
  value,
  onChange,
  placeholder = "Select",
  showCount = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selected = options.find((opt) => opt.value === value) || options[0];

  // Filter options based on search term
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm("");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2.5 px-4 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
      >
        <span className="truncate">
          {selected?.label || placeholder}
          {showCount && selected?.count !== undefined && (
            <span className="ml-2 text-xs text-gray-500">
              ({selected.count})
            </span>
          )}
        </span>
        {isOpen ? (
          <ChevronUp size={16} className="ml-2 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown size={16} className="ml-2 text-gray-500 flex-shrink-0" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          {/* Search Box (only show if more than 5 options) */}
          {options.length > 5 && (
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Options List */}
          <ul className="py-2 text-sm text-gray-700 max-h-64 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <li key={opt.value}>
                  <button
                    className={`w-full text-left px-4 py-2.5 hover:bg-primary/10 transition-colors flex items-center justify-between ${
                      opt.value === value ? "bg-primary/5 text-primary" : ""
                    }`}
                    onClick={() => handleSelect(opt)}
                  >
                    <span className="truncate">{opt.label}</span>
                    {showCount && opt.count !== undefined && (
                      <span className="ml-2 text-xs text-gray-500 flex-shrink-0">
                        {opt.count}
                      </span>
                    )}
                  </button>
                </li>
              ))
            ) : (
              <li className="px-4 py-3 text-center text-gray-500 text-xs">
                No results found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
